/* ==========================================================
   ma-collection.js — Load More / Infinite Scroll
   Works alongside Dawn's facets.js AJAX filtering.
   ========================================================== */

(function () {
  'use strict';

  class CollectionPagination {
    constructor(container) {
      this.container   = container;
      this.mode        = container.dataset.paginationMode || 'paginated';
      this.loadLabel   = container.dataset.loadMoreLabel  || 'Load more products';
      this.isLoading   = false;
      this.abortCtrl   = null;
      this.wrapper     = null;
      this.sentinel    = null;
      this._onScroll   = null;

      if (this.mode === 'paginated') return;

      this.init();
      this.watchFacetsUpdates();
    }

    // ── Accessors ─────────────────────────────────────────────

    get grid()       { return this.container.querySelector('#product-grid'); }
    get meta()       { return this.container.querySelector('.ma-pagination-meta'); }
    get currentPage(){ return parseInt(this.meta?.dataset.currentPage || '1', 10); }
    get totalPages() { return parseInt(this.meta?.dataset.totalPages  || '1', 10); }
    get hasMore()    { return this.currentPage < this.totalPages; }

    // ── Init ──────────────────────────────────────────────────

    init() {
      // Remove native pagination — we're replacing it
      this.container.querySelectorAll('.pagination-wrapper').forEach((el) => el.remove());

      if (!this.hasMore) return;

      if (this.mode === 'load_more') {
        this.initLoadMore();
      } else if (this.mode === 'infinite') {
        this.initInfinite();
      }
    }

    // ── Load More button ──────────────────────────────────────

    initLoadMore() {
      this.wrapper = document.createElement('div');
      this.wrapper.className = 'ma-load-more';
      this.wrapper.innerHTML = `
        <button class="ma-load-more__btn button button--secondary" type="button">
          <span class="ma-load-more__label">${this.loadLabel}</span>
          <span class="ma-load-more__spinner" hidden aria-hidden="true">
            <svg class="loading__spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
              <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
            </svg>
          </span>
        </button>
      `;

      const collectionEl = this.container.querySelector('.collection');
      if (collectionEl) collectionEl.appendChild(this.wrapper);

      this.wrapper.querySelector('.ma-load-more__btn').addEventListener('click', () => this.loadNext());
    }

    // ── Infinite scroll ───────────────────────────────────────

    initInfinite() {
      this.sentinel = document.createElement('div');
      this.sentinel.className = 'ma-infinite-sentinel';
      this.sentinel.setAttribute('aria-hidden', 'true');

      const collectionEl = this.container.querySelector('.collection');
      if (collectionEl) collectionEl.appendChild(this.sentinel);

      // Use scroll events — more reliable than IntersectionObserver
      // which can silently fail to fire when layout isn't settled at observe() time.
      const check = () => {
        if (this.isLoading || !this.hasMore || !this.sentinel) return;
        const rect = this.sentinel.getBoundingClientRect();
        if (rect.top <= window.innerHeight + 600) {
          this.loadNext();
        }
      };

      this._onScroll = check;
      window.addEventListener('scroll', this._onScroll, { passive: true });

      // Check immediately in case products don't fill the viewport
      check();
    }

    // ── Fetch next page ───────────────────────────────────────

    buildUrl(page) {
      const params = new URLSearchParams(window.location.search);
      params.set('page', page);
      // Do NOT add section_id — full-page fetch ensures the paginate tag
      // correctly reads the page param. Section rendering mode can return
      // stale page-1 pagination data, causing currentPage to never advance.
      return `${window.location.pathname}?${params.toString()}`;
    }

    loadNext() {
      if (this.isLoading || !this.hasMore) return;

      const nextPage = this.currentPage + 1;

      // Cancel any previous in-flight request
      if (this.abortCtrl) this.abortCtrl.abort();
      this.abortCtrl = new AbortController();

      this.isLoading = true;
      this.setLoadingUI(true);

      fetch(this.buildUrl(nextPage), { signal: this.abortCtrl.signal })
        .then((r) => r.text())
        .then((html) => {
          const doc      = new DOMParser().parseFromString(html, 'text/html');
          const newItems = doc.querySelectorAll('#product-grid .grid__item');
          const newMeta  = doc.querySelector('.ma-pagination-meta');
          const grid     = this.grid;

          if (!grid) return;

          newItems.forEach((item) => {
            // Cancel scroll animations on appended items — they're already partially visible
            item.classList.remove('scroll-trigger', 'animate--slide-in');
            item.removeAttribute('data-cascade');
            item.style.removeProperty('--animation-order');
            grid.appendChild(item);
          });

          // Update page counter from server response
          if (this.meta && newMeta) {
            this.meta.dataset.currentPage = newMeta.dataset.currentPage;
            this.meta.dataset.totalPages  = newMeta.dataset.totalPages;
          }

          renderCountryFlags();
          if (!this.hasMore) this.exhausted();
        })
        .catch((err) => {
          if (err.name !== 'AbortError') console.warn('[ma-collection]', err);
        })
        .finally(() => {
          this.isLoading = false;
          this.setLoadingUI(false);
        });
    }

    setLoadingUI(loading) {
      if (this.mode === 'load_more' && this.wrapper) {
        const btn     = this.wrapper.querySelector('.ma-load-more__btn');
        const spinner = this.wrapper.querySelector('.ma-load-more__spinner');
        const label   = this.wrapper.querySelector('.ma-load-more__label');
        if (btn)     btn.disabled    = loading;
        if (spinner) spinner.hidden  = !loading;
        if (label)   label.hidden    = loading;
      }
    }

    exhausted() {
      if (this.mode === 'load_more' && this.wrapper) {
        this.wrapper.remove();
        this.wrapper = null;
      }
      if (this.mode === 'infinite') {
        if (this._onScroll) {
          window.removeEventListener('scroll', this._onScroll);
          this._onScroll = null;
        }
        this.sentinel?.remove();
        this.sentinel = null;
      }
    }

    // ── Reset when facets.js updates the grid ─────────────────

    watchFacetsUpdates() {
      // facets.js sets #ProductGridContainer.innerHTML which changes its direct children
      new MutationObserver(() => {
        // Abort any in-flight fetch
        if (this.abortCtrl) { this.abortCtrl.abort(); this.abortCtrl = null; }

        // Clean up existing UI
        this.wrapper?.remove();    this.wrapper  = null;
        this.sentinel?.remove();   this.sentinel = null;
        if (this._onScroll) {
          window.removeEventListener('scroll', this._onScroll);
          this._onScroll = null;
        }
        this.isLoading = false;

        // Re-initialize with fresh page 1 data
        this.init();
      }).observe(this.container, { childList: true });
    }
  }

  // ── Country badge flag emoji ──────────────────────────────

  var NAME_TO_CODE = {
    'china': 'CN', 'japan': 'JP', 'south korea': 'KR', 'korea': 'KR',
    'taiwan': 'TW', 'thailand': 'TH', 'philippines': 'PH', 'vietnam': 'VN',
    'indonesia': 'ID', 'malaysia': 'MY', 'singapore': 'SG', 'india': 'IN',
    'united states': 'US', 'usa': 'US', 'uk': 'GB', 'united kingdom': 'GB',
    'mexico': 'MX', 'canada': 'CA', 'australia': 'AU', 'france': 'FR',
    'germany': 'DE', 'italy': 'IT', 'spain': 'ES', 'brazil': 'BR',
    'peru': 'PE', 'colombia': 'CO', 'argentina': 'AR', 'chile': 'CL',
    'hong kong': 'HK', 'turkey': 'TR', 'russia': 'RU', 'pakistan': 'PK',
    'ireland': 'IE', 'hawaii': 'US'
  };

  function codeToEmoji(code) {
    if (!code || !/^[A-Za-z]{2}$/.test(code)) return null;
    return Array.from(code.toUpperCase()).map(function(c) {
      return String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65);
    }).join('');
  }

  function renderCountryFlags() {
    document.querySelectorAll('.ma-country-badge').forEach(function(el) {
      var flagEl = el.querySelector('.ma-country-badge__flag');
      if (!flagEl || flagEl.textContent) return; // already set
      var country = el.dataset.country || '';
      var emoji = codeToEmoji(country) || codeToEmoji(NAME_TO_CODE[country.toLowerCase().trim()]);
      if (emoji) flagEl.textContent = emoji;
    });
  }

  // ── Flat mobile filter panel ─────────────────────────────────
  // Dawn removes `open` from inner <details> when the drawer closes.
  // Re-add it whenever the drawer opens or facets re-render via AJAX.

  function initFlatMobileFacets() {
    function openAllFilterDetails() {
      document.querySelectorAll('.mobile-facets__details').forEach(function(d) {
        d.open = true;
      });
    }

    openAllFilterDetails();

    // Re-open after AJAX re-renders (Dawn replaces FacetsWrapperMobile children)
    var facetsWrapper = document.getElementById('FacetsWrapperMobile');
    if (facetsWrapper) {
      new MutationObserver(function(mutations) {
        if (mutations.some(function(m) { return m.addedNodes.length > 0; })) {
          openAllFilterDetails();
        }
      }).observe(facetsWrapper, { childList: true });
    }

    // Re-open when the outer Filter & Sort drawer is opened
    var disclosure = document.querySelector('.mobile-facets__disclosure');
    if (disclosure) {
      disclosure.addEventListener('toggle', function() {
        if (disclosure.open) openAllFilterDetails();
      });
    }
  }

  // ── Mobile Filter + Sort bar ─────────────────────────────────

  class CollectionFilterSortBar {
    constructor() {
      this.bar = document.querySelector('.ma-filter-sort-bar');
      if (!this.bar) return;

      this.filterBtn   = this.bar.querySelector('.js-ma-open-filters');
      this.sortBtn     = this.bar.querySelector('.js-ma-open-sort');
      this.sortValueEl = this.bar.querySelector('.ma-fsbar-sort-value');
      this.countEl     = this.bar.querySelector('.ma-fsbar-count');
      this.sortSheet   = null;

      this.init();
    }

    init() {
      if (this.filterBtn) {
        this.filterBtn.addEventListener('click', () => this.openFilters());
      }
      if (this.sortBtn) {
        this.sortBtn.addEventListener('click', () => this.openSort());
      }

      // Re-sync after Dawn's AJAX filter updates (grid content replaced)
      const gridContainer = document.getElementById('ProductGridContainer');
      if (gridContainer) {
        new MutationObserver(() => {
          // Dawn's AJAX updates the URL — re-read on next tick
          setTimeout(() => this.syncFilterCount(), 30);
        }).observe(gridContainer, { childList: true });
      }

      // Sync on browser back/forward navigation
      window.addEventListener('popstate', () => this.syncFilterCount());
    }

    openFilters() {
      // Trigger Dawn's menu-drawer by clicking its summary toggle button
      var btn = document.querySelector('.mobile-facets__open-wrapper');
      if (btn) btn.click();
    }

    openSort() {
      if (!this.sortSheet) this._buildSortSheet();
      if (this.sortSheet) this.sortSheet.showModal();
    }

    _buildSortSheet() {
      // Read sort options from the page's hidden sort select
      var select = document.querySelector('select[name="sort_by"]');
      if (!select) return;

      var currentValue = select.value;
      var sheet = document.createElement('dialog');
      sheet.className = 'ma-sort-sheet';

      var optionsHtml = Array.from(select.options).map(function(opt) {
        var active = opt.value === currentValue;
        return (
          '<button class="ma-sort-sheet__option' + (active ? ' ma-sort-sheet__option--active' : '') + '" ' +
          'data-value="' + opt.value + '" type="button">' +
          '<span>' + opt.text + '</span>' +
          (active ? '<span class="ma-sort-sheet__check" aria-hidden="true">✓</span>' : '') +
          '</button>'
        );
      }).join('');

      sheet.innerHTML =
        '<div class="ma-sort-sheet__handle" aria-hidden="true"></div>' +
        '<div class="ma-sort-sheet__header">' +
          '<span class="ma-sort-sheet__title">Sort by</span>' +
          '<button class="ma-sort-sheet__close" type="button" aria-label="Close">✕</button>' +
        '</div>' +
        '<div class="ma-sort-sheet__body">' + optionsHtml + '</div>';

      document.body.appendChild(sheet);
      this.sortSheet = sheet;

      var self = this;

      sheet.querySelector('.ma-sort-sheet__close').addEventListener('click', function() {
        sheet.close();
      });

      // Backdrop click
      sheet.addEventListener('click', function(e) {
        if (e.target === sheet) sheet.close();
      });

      // Option click → navigate
      sheet.querySelectorAll('.ma-sort-sheet__option').forEach(function(btn) {
        btn.addEventListener('click', function() {
          var value = btn.dataset.value;
          var label = btn.querySelector('span').textContent.trim();
          sheet.close();
          self._applySort(value, label);
        });
      });
    }

    _applySort(value, label) {
      if (this.sortValueEl) this.sortValueEl.textContent = label;
      var params = new URLSearchParams(window.location.search);
      params.set('sort_by', value);
      window.location.search = params.toString();
    }

    syncFilterCount() {
      // Count from Dawn's active-facets chips — matches Liquid filter.active_values logic exactly
      var count = document.querySelectorAll(
        '.active-facets-desktop .active-facets__button--light'
      ).length;

      if (this.filterBtn) {
        if (count > 0) {
          this.filterBtn.classList.add('ma-fsbar-btn--active');
          if (this.countEl) this.countEl.textContent = ' · ' + count;
        } else {
          this.filterBtn.classList.remove('ma-fsbar-btn--active');
          if (this.countEl) this.countEl.textContent = '';
        }
      }
    }
  }

  // ── Boot ────────────────────────────────────────────────────

  function boot() {
    renderCountryFlags();
    const container = document.getElementById('ProductGridContainer');
    if (container) new CollectionPagination(container);
    initFlatMobileFacets();
    new CollectionFilterSortBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
